import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TRANSFER_STATUS_LABELS, transferSchema, type ProductDTO, type TransferDTO, type TransferStatus } from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { ArrowRight, PackageCheck, Plus, Trash2, Truck } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { useProducts } from '../lib/catalog';
import { formatDateTime, formatMoney } from '../lib/format';
import { useSession } from '../lib/session';
import { formatQty, fromInternalQty, toInternalQty } from '../lib/units';

const TONE: Record<TransferStatus, 'warning' | 'success' | 'neutral'> = { IN_TRANSIT: 'warning', RECEIVED: 'success', CANCELED: 'neutral' };

/**
 * Transferencias de mercadería entre locales propios. Sale del stock del local que envía y queda
 * "en camino" hasta que el local que recibe confirma lo que llegó. Si falta algo, se ve.
 */
export function TransfersPage() {
  const { me } = useSession();
  const [status, setStatus] = useState<TransferStatus | ''>('');
  const [creating, setCreating] = useState(false);
  const [receiving, setReceiving] = useState<TransferDTO | null>(null);
  const [canceling, setCanceling] = useState<TransferDTO | null>(null);
  const list = useQuery({
    queryKey: ['transfers', status],
    queryFn: () => api.get<TransferDTO[]>(`/transfers${status ? `?status=${status}` : ''}`),
  });
  const myBranch = me.membership.branchId;
  const pending = (list.data ?? []).filter((t) => t.status === 'IN_TRANSIT' && (!myBranch || t.destBranchId === myBranch)).length;

  return (
    <>
      <PageHeader
        title="Transferencias"
        subtitle="Mandá mercadería de un local a otro. Sale del stock del que envía y entra al otro cuando confirma lo que llegó."
        actions={
          <Button variant="primary" icon={<Truck />} onClick={() => setCreating(true)}>
            Nueva transferencia
          </Button>
        }
      />
      {pending > 0 && (
        <Callout tone="warning">
          {pending === 1 ? 'Hay 1 transferencia en camino' : `Hay ${pending} transferencias en camino`} para recibir. Cuando llegue la mercadería,
          confirmá lo que vino.
        </Callout>
      )}
      <div className="bf-chips" style={{ margin: '4px 0 12px' }}>
        {(['', 'IN_TRANSIT', 'RECEIVED', 'CANCELED'] as const).map((s) => (
          <button key={s || 'all'} type="button" className="bf-chip" aria-pressed={status === s} onClick={() => setStatus(s)}>
            {s ? TRANSFER_STATUS_LABELS[s] : 'Todas'}
          </button>
        ))}
      </div>
      {list.isPending ? (
        <Skeleton style={{ height: 200 }} />
      ) : !list.data?.length ? (
        <Card>
          <CardBody>
            <EmptyState icon={<Truck />} title="Sin transferencias" text="Cuando mandes mercadería a otro local, aparece acá." />
          </CardBody>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {list.data.map((t) => {
            const canReceive = t.status === 'IN_TRANSIT' && (!myBranch || myBranch === t.destBranchId);
            const canCancel = t.status === 'IN_TRANSIT' && (!myBranch || myBranch === t.originBranchId);
            return (
              <Card key={t.id}>
                <CardBody>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div className="bf-cell-main" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        #{t.number} · {t.originBranchName} <ArrowRight size={16} aria-label="a" /> {t.destBranchName}
                        <Badge tone={TONE[t.status]}>{TRANSFER_STATUS_LABELS[t.status]}</Badge>
                        {t.missingLines > 0 && <Badge tone="danger">Llegó con diferencias</Badge>}
                      </div>
                      <div className="bf-cell-sub">
                        Enviada {formatDateTime(t.sentAt)}
                        {t.sentByName ? ` por ${t.sentByName}` : ''}
                        {t.receivedAt ? ` · Recibida ${formatDateTime(t.receivedAt)}${t.receivedByName ? ` por ${t.receivedByName}` : ''}` : ''}
                        {t.sentCostCents !== null ? ` · Costo ${formatMoney(t.sentCostCents)}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {canCancel && (
                        <Button size="sm" variant="ghost" onClick={() => setCanceling(t)}>
                          Cancelar
                        </Button>
                      )}
                      {canReceive && (
                        <Button size="sm" variant="primary" icon={<PackageCheck />} onClick={() => setReceiving(t)}>
                          Recibir
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="bf-table-wrap" style={{ marginTop: 10 }}>
                    <table className="bf-table">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th style={{ textAlign: 'right' }}>Enviado</th>
                          <th style={{ textAlign: 'right' }}>Recibido</th>
                        </tr>
                      </thead>
                      <tbody>
                        {t.items.map((i) => {
                          const diff = i.qtyReceived !== null && i.qtyReceived < i.qtySent;
                          return (
                            <tr key={i.id}>
                              <td>{i.productName}</td>
                              <td className="bf-num" style={{ textAlign: 'right' }}>
                                {formatQty(i.unitType, i.qtySent)}
                              </td>
                              <td className={`bf-num${diff ? ' bf-text-danger' : ''}`} style={{ textAlign: 'right' }}>
                                {i.qtyReceived === null ? '—' : formatQty(i.unitType, i.qtyReceived)}
                                {diff && ` (faltó ${formatQty(i.unitType, i.qtySent - i.qtyReceived!)})`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {t.notes && (
                    <p className="bf-cell-sub" style={{ margin: '8px 0 0', whiteSpace: 'pre-line' }}>
                      {t.notes}
                    </p>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
      {creating && <NewTransferDialog onClose={() => setCreating(false)} />}
      {receiving && <ReceiveDialog transfer={receiving} onClose={() => setReceiving(null)} />}
      {canceling && <CancelDialog transfer={canceling} onClose={() => setCanceling(null)} />}
    </>
  );
}

type Line = { key: number; productId: string; qty: string };

function NewTransferDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { me, selectedBranchId } = useSession();
  const branches = useQuery({ queryKey: ['transfers', 'branches'], queryFn: () => api.get<{ id: string; name: string }[]>('/transfers/branches') });
  const products = useProducts();
  const myBranch = me.membership.branchId;
  const [origin, setOrigin] = useState(myBranch ?? selectedBranchId ?? me.branches.find((b) => b.isHeadquarters)?.id ?? '');
  const [dest, setDest] = useState('');
  const [lines, setLines] = useState<Line[]>([{ key: 1, productId: '', qty: '' }]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const list = useMemo(() => (products.data ?? []).filter((p) => p.isActive && p.tracksStock), [products.data]);
  const byId = (id: string): ProductDTO | undefined => list.find((p) => p.id === id);
  const all = branches.data ?? [];
  const originName = all.find((b) => b.id === origin)?.name;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = transferSchema.safeParse({
      originBranchId: origin,
      destBranchId: dest,
      items: lines
        .filter((l) => l.productId)
        .map((l) => {
          const p = byId(l.productId);
          return { productId: l.productId, qty: p ? (toInternalQty(p.unitType, l.qty || '0') ?? 0) : 0 };
        }),
      notes,
    });
    if (!dest) return setError('Elegí a qué local va.');
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá los datos.');
    setBusy(true);
    setError(null);
    try {
      const t = await api.post<TransferDTO>('/transfers', parsed.data);
      await Promise.all(['transfers', 'stock', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success(`Transferencia #${t.number} en camino`, `Cuando llegue, ${t.destBranchName} confirma lo que recibió.`);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Nueva transferencia"
      description="La mercadería sale ya del stock del local que envía, con su costo y vencimiento."
      wide
    >
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-row">
          <Field label="Sale de">
            {myBranch ? (
              <Input value={originName ?? 'Mi local'} readOnly />
            ) : (
              <Select value={origin} onChange={(e) => setOrigin(e.target.value)}>
                {all.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Va a">
            <Select value={dest} onChange={(e) => setDest(e.target.value)} autoFocus>
              <option value="">Elegí el local</option>
              {all
                .filter((b) => b.id !== origin)
                .map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
            </Select>
          </Field>
        </div>
        {lines.map((l, n) => {
          const p = byId(l.productId);
          return (
            <div key={l.key} className="bf-form-row" style={{ alignItems: 'end' }}>
              <Field label={n === 0 ? 'Producto' : undefined}>
                <Select
                  value={l.productId}
                  onChange={(e) => setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, productId: e.target.value } : x)))}
                >
                  <option value="">Elegí el producto</option>
                  {list.map((x) => (
                    <option key={x.id} value={x.id} disabled={lines.some((o) => o.key !== l.key && o.productId === x.id)}>
                      {x.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={n === 0 ? 'Cantidad' : undefined} hint={p?.unitType === 'WEIGHT' && n === 0 ? 'En kilos. Ej.: 12,500' : undefined}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Input
                    inputMode="decimal"
                    value={l.qty}
                    placeholder={p ? (p.unitType === 'UNIT' ? 'Unidades' : 'Kilos') : ''}
                    onChange={(e) => setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, qty: e.target.value } : x)))}
                  />
                  {lines.length > 1 && (
                    <Button
                      variant="ghost"
                      icon={<Trash2 />}
                      aria-label="Quitar"
                      onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                    />
                  )}
                </div>
              </Field>
            </div>
          );
        })}
        <div>
          <Button
            size="sm"
            icon={<Plus />}
            onClick={() => setLines((ls) => [...ls, { key: Math.max(...ls.map((x) => x.key)) + 1, productId: '', qty: '' }])}
          >
            Agregar producto
          </Button>
        </div>
        <Field label="Nota (opcional)">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej.: para el fin de semana largo" />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" icon={<Truck />} loading={busy}>
            Enviar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ReceiveDialog({ transfer, onClose }: { transfer: TransferDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [qty, setQty] = useState<Record<string, string>>(() =>
    Object.fromEntries(transfer.items.map((i) => [i.id, fromInternalQty(i.unitType, i.qtySent)])),
  );
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const items = transfer.items.map((i) => ({ itemId: i.id, qtyReceived: toInternalQty(i.unitType, qty[i.id] || '0') ?? -1 }));
    const bad = transfer.items.find((i, n) => items[n]!.qtyReceived < 0 || items[n]!.qtyReceived > i.qtySent);
    if (bad) return setError(`Revisá ${bad.productName}: no puede ser más de lo que se envió.`);
    setBusy(true);
    setError(null);
    try {
      const t = await api.post<TransferDTO>(`/transfers/${transfer.id}/receive`, { items, notes });
      await Promise.all(['transfers', 'stock', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success('Mercadería recibida', t.missingLines ? 'Quedó registrada la diferencia.' : 'Ya está en tu stock.');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Recibir transferencia #${transfer.number}`}
      description={`De ${transfer.originBranchName}. Pesá y contá lo que llegó; si falta algo, corregí la cantidad.`}
    >
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        {transfer.items.map((i) => (
          <Field key={i.id} label={`${i.productName} · enviado ${formatQty(i.unitType, i.qtySent)}`}>
            <Input inputMode="decimal" value={qty[i.id] ?? ''} onChange={(e) => setQty((q) => ({ ...q, [i.id]: e.target.value }))} />
          </Field>
        ))}
        <Field label="Nota (opcional)">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej.: llegó un bife golpeado" />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Volver
          </Button>
          <Button variant="primary" icon={<PackageCheck />} loading={busy} onClick={() => void submit()}>
            Confirmar recepción
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function CancelDialog({ transfer, onClose }: { transfer: TransferDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    try {
      await api.post(`/transfers/${transfer.id}/cancel`, {});
      await Promise.all(['transfers', 'stock'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Cancelar transferencia #${transfer.number}`}
      description={`La mercadería vuelve al stock de ${transfer.originBranchName} tal como salió.`}
    >
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Volver
          </Button>
          <Button variant="danger" loading={busy} onClick={() => void submit()}>
            Cancelar transferencia
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
